import { DateTime } from "luxon"

import { formatTableName } from "@/shared/tables"
import { type OpeningHour } from "@/utils/opening_hours"

import { RESERVATION_DURATION_STEP_SECONDS } from "./reservation_duration_options"
import { getSidebarOpeningRange, type SidebarTableBusyBlock } from "./reservation_sidebar_availability"
import {
  findFreeGaps,
  getRoundedBookingThreshold,
  groupBusyBlocksByTable,
  matchesTableCapacity,
  type AvailabilityInterval,
} from "./reservation_table_availability"

export const DEFAULT_BEST_PICKS_LIMIT = 6

export type BestPicksTable = {
  id: string
  name: string
  tableGroup?: { name?: string | null } | null
  minCapacity?: number | null
  maxCapacity?: number | null
}

export type ReservationBestPick = {
  tableId: string
  tableName: string
  startTime: string
  endTime: string
}

export type BestPicksRanker = (picks: ReservationBestPick[]) => ReservationBestPick[]

type ReservationBestPicksInput = {
  tables: BestPicksTable[]
  partySize: number
  date: DateTime | null
  openingHours?: OpeningHour[]
  busyBlocks?: SidebarTableBusyBlock[]
  timezone: string
  minReservationTime?: number | null
  maxReservationTime?: number | null
  minAdvanceBookingTime?: number | null
  limit?: number
  rankPicks?: BestPicksRanker
}

type Interval = AvailabilityInterval

/**
 * Default ranking: surface the tables whose free window opens earliest first. Kept as a named,
 * swappable function so the ranking strategy can be replaced without touching the gap finder.
 */
export const rankBestPicksByEarliestStart: BestPicksRanker = (picks) =>
  [...picks].sort((a, b) => {
    const startDiff = DateTime.fromISO(a.startTime).toMillis() - DateTime.fromISO(b.startTime).toMillis()
    if (startDiff !== 0) return startDiff

    return a.tableName.localeCompare(b.tableName) || a.tableId.localeCompare(b.tableId)
  })

/**
 * The earliest bookable window for a single table: the first free gap that is at least
 * `minDurationSeconds` long, with its end capped at `maxDurationSeconds`. Returns null when the
 * table has no gap long enough. `bounds` is the [lower, upper] search range already narrowed to
 * opening hours and the min-advance threshold.
 */
const buildTablePick = (
  table: BestPicksTable,
  busy: Interval[],
  bounds: Interval,
  minDurationSeconds: number,
  maxDurationSeconds: number,
): ReservationBestPick | null => {
  const gaps = findFreeGaps(bounds.start, bounds.end, busy)
  const window = gaps.find((gap) => gap.end.diff(gap.start, "seconds").seconds >= minDurationSeconds)
  if (!window) return null

  const availableSeconds = window.end.diff(window.start, "seconds").seconds
  const windowEnd = window.start.plus({ seconds: Math.min(availableSeconds, maxDurationSeconds) })

  const startTime = window.start.toISO()
  const endTime = windowEnd.toISO()
  if (!startTime || !endTime) return null

  return {
    tableId: table.id,
    tableName: formatTableName(table, { includeGroupName: false }),
    startTime,
    endTime,
  }
}

/**
 * Client-side "Best available picks" heuristic (behavior board 1283-51228). For each
 * capacity-matching table it finds the earliest contiguous free window (>= min duration) inside
 * opening hours after the min-advance-booking threshold, excluding that table's busy blocks, and
 * caps the window at the max reservation duration. Tables are then ranked (default: earliest window
 * first) and the top `limit` (~6) suggestions are returned.
 */
export const getReservationBestPicks = ({
  tables,
  partySize,
  date,
  openingHours,
  busyBlocks = [],
  timezone,
  minReservationTime,
  maxReservationTime,
  minAdvanceBookingTime,
  limit = DEFAULT_BEST_PICKS_LIMIT,
  rankPicks = rankBestPicksByEarliestStart,
}: ReservationBestPicksInput): ReservationBestPick[] => {
  const openingRange = getSidebarOpeningRange(date, openingHours, timezone)
  if (!date || !openingRange || partySize <= 0) return []

  const minDurationSeconds =
    minReservationTime && minReservationTime > 0 ? minReservationTime : RESERVATION_DURATION_STEP_SECONDS
  const maxDurationSeconds =
    maxReservationTime && maxReservationTime >= minDurationSeconds ? maxReservationTime : minDurationSeconds

  const bookingThreshold = minAdvanceBookingTime
    ? getRoundedBookingThreshold(DateTime.now().setZone(timezone).plus({ seconds: minAdvanceBookingTime }))
    : DateTime.now().setZone(timezone)

  const bounds: Interval = {
    start: bookingThreshold > openingRange.opensAt ? bookingThreshold : openingRange.opensAt,
    end: openingRange.closesAt,
  }

  const busyByTable = groupBusyBlocksByTable(busyBlocks)

  const picks = tables
    .filter((table) => matchesTableCapacity(table, partySize))
    .map((table) => buildTablePick(table, busyByTable[table.id] || [], bounds, minDurationSeconds, maxDurationSeconds))
    .filter((pick): pick is ReservationBestPick => pick !== null)

  return rankPicks(picks).slice(0, limit)
}
